import makerjs from 'makerjs'
import Decimal from 'decimal.js'

// kh.svg 中的形状元素（SVG 坐标，单位: point, 1mm = 2.83465pt）
const SCALE = 1 / 2.83465 // point -> mm

// 原始 SVG 所有元素的边界框中心（SVG 坐标）
// 计算自实际路径点：
// X: min≈290.27, max≈336.1 → CX≈313.185
// Y: min≈209.46, max≈226.46 → CY≈217.96
const CX = 313.185
const CY = 217.96

// 将 SVG 坐标转为以中心为原点的 mm 坐标（Y轴翻转）
function toMM(svgX, svgY) {
    return [(svgX - CX) * SCALE, -(svgY - CY) * SCALE]
}

/**
 * 生成单个底棉模型（以原点为中心）
 * 形状来源: public/kh.svg（真实路径）
 */
function generateSingleFoam() {
    const foam = { paths: {}, models: {} }

    // ========== 1. 左侧折线 ==========
    // polyline: 311.65 215.13 → 297.64 215.13 → 297.64 218.16 → 290.27 218.16 → 290.27 226.46 → 323.09 226.46
    const leftPts = [
        toMM(311.65, 215.13),
        toMM(297.64, 215.13),
        toMM(297.64, 218.16),
        toMM(290.27, 218.16),
        toMM(290.27, 226.46),
        toMM(323.09, 226.46),
    ]
    for (let i = 0; i < leftPts.length - 1; i++) {
        foam.paths['left_seg' + i] = new makerjs.paths.Line(leftPts[i], leftPts[i + 1])
    }

    // ========== 2. 右侧折线 ==========
    // polyline: 317.32 209.46 → 336.1 209.46 → 336.1 217.98 → 328.76 217.99 → 328.76 220.79
    const rightPts = [
        toMM(317.32, 209.46),
        toMM(336.1, 209.46),
        toMM(336.1, 217.98),
        toMM(328.76, 217.99),
        toMM(328.76, 220.79),
    ]
    for (let i = 0; i < rightPts.length - 1; i++) {
        foam.paths['right_seg' + i] = new makerjs.paths.Line(rightPts[i], rightPts[i + 1])
    }

    // ========== 3. 左上圆弧 ==========
    // M317.32,209.46 c0,3.13 -2.54,5.67 -5.67,5.67
    // 这是从 (317.32,209.46) 到 (311.65,215.13) 的四分之一圆（半径 5.67）
    // 圆心在 (317.32, 215.13)
    {
        const p0 = toMM(317.32, 209.46)
        const c1 = toMM(317.32, 209.46 + 3.13)
        const c2 = toMM(317.32 - 2.54, 209.46 + 5.67)
        const p3 = toMM(317.32 - 5.67, 209.46 + 5.67) // = (311.65, 215.13)
        foam.models['arc_top'] = new makerjs.models.BezierCurve(p0, c1, c2, p3)
    }

    // ========== 4. 右下圆弧 ==========
    // M328.76,220.79 c0,3.13 -2.54,5.67 -5.67,5.67
    // 从 (328.76,220.79) 到 (323.09,226.46) 的四分之一圆（半径 5.67）
    // 圆心在 (328.76, 226.46)
    {
        const p0 = toMM(328.76, 220.79)
        const c1 = toMM(328.76, 220.79 + 3.13)
        const c2 = toMM(328.76 - 2.54, 220.79 + 5.67)
        const p3 = toMM(328.76 - 5.67, 220.79 + 5.67) // = (323.09, 226.46)
        foam.models['arc_bottom'] = new makerjs.models.BezierCurve(p0, c1, c2, p3)
    }

    return foam
}

/**
 * 为所有按键生成底棉布局
 * @param {Array} keysArray - KLE 解析后的 Key 对象数组
 * @param {Object} options - { unitWidth, unitHeight, extraRotation, boardRadius }
 * @returns {Object} makerjs 模型
 */
export function buildFoams(keysArray, options) {
    const {
        unitWidth = new Decimal(19.05),
        unitHeight = new Decimal(19.05),
        extraRotation = 0,  // 额外旋转角度（下灯位时传180）
        boardRadius = new Decimal(2),
    } = options

    const canvas = { models: {} }
    const singleFoam = generateSingleFoam()

    let minX = Number.POSITIVE_INFINITY
    let minY = Number.POSITIVE_INFINITY
    let maxX = Number.NEGATIVE_INFINITY
    let maxY = Number.NEGATIVE_INFINITY

    let id = 0
    for (const key of keysArray) {
        // 计算按键中心位置（mm）
        const originX = key.centerX.times(unitWidth).toNumber()
        const originY = key.centerY.times(unitHeight).times(-1).toNumber()

        // 克隆底棉模型
        const foamInstance = makerjs.model.clone(singleFoam)

        // 应用旋转：键自身旋转（取负）+ 额外旋转（灯位）
        const angle = key.angle.times(-1).toNumber() + extraRotation
        if (angle !== 0) {
            makerjs.model.rotate(foamInstance, angle)
        }

        // 偏移先于旋转：偏移向量随按键角度旋转
        const angleRad = angle * Math.PI / 180
        const offsetX = 0.62 * Math.cos(angleRad) - (-3.87) * Math.sin(angleRad)
        const offsetY = 0.62 * Math.sin(angleRad) + (-3.87) * Math.cos(angleRad)
        foamInstance.origin = [originX + offsetX, originY + offsetY]

        canvas.models['Foam_' + id] = foamInstance

        // 追踪边界（单位矩形旋转后的轴对齐包围盒）
        const halfW = key.width.times(unitWidth).times(0.5).toNumber()
        const halfH = key.height.times(unitHeight).times(0.5).toNumber()
        const rad = angle * Math.PI / 180
        const cosA = Math.cos(rad)
        const sinA = Math.sin(rad)
        const corners = [
            [originX + (-halfW * cosA - (-halfH) * sinA), originY + (-halfW * sinA + (-halfH) * cosA)],
            [originX + (halfW * cosA - (-halfH) * sinA), originY + (halfW * sinA + (-halfH) * cosA)],
            [originX + (halfW * cosA - halfH * sinA), originY + (halfW * sinA + halfH * cosA)],
            [originX + (-halfW * cosA - halfH * sinA), originY + (-halfW * sinA + halfH * cosA)],
        ]
        for (const [cx, cy] of corners) {
            if (cx < minX) minX = cx
            if (cx > maxX) maxX = cx
            if (cy < minY) minY = cy
            if (cy > maxY) maxY = cy
        }

        id++
    }

    // 添加整体边框（与定位板一致）
    if (id > 0) {
        const boardRadiusNum = boardRadius ? boardRadius.toNumber() : 0
        const r = Math.min(boardRadiusNum, (maxX - minX) / 2, (maxY - minY) / 2)

        if (r > 0) {
            // 圆角边框
            canvas.models['BoundingBox'] = {
                paths: {
                    // 四条直线边
                    lineTop: new makerjs.paths.Line([minX + r, maxY], [maxX - r, maxY]),
                    lineBottom: new makerjs.paths.Line([minX + r, minY], [maxX - r, minY]),
                    lineLeft: new makerjs.paths.Line([minX, maxY - r], [minX, minY + r]),
                    lineRight: new makerjs.paths.Line([maxX, maxY - r], [maxX, minY + r]),
                    // 四个圆角
                    arcTopRight: new makerjs.paths.Arc([maxX - r, maxY - r], r, 0, 90),
                    arcBottomRight: new makerjs.paths.Arc([maxX - r, minY + r], r, 270, 360),
                    arcBottomLeft: new makerjs.paths.Arc([minX + r, minY + r], r, 180, 270),
                    arcTopLeft: new makerjs.paths.Arc([minX + r, maxY - r], r, 90, 180)
                }
            }
        } else {
            // 直角边框
            canvas.models['BoundingBox'] = {
                paths: {
                    lineTop: new makerjs.paths.Line([minX, maxY], [maxX, maxY]),
                    lineBottom: new makerjs.paths.Line([minX, minY], [maxX, minY]),
                    lineLeft: new makerjs.paths.Line([minX, maxY], [minX, minY]),
                    lineRight: new makerjs.paths.Line([maxX, maxY], [maxX, minY])
                }
            }
        }
    }

    return canvas
}